import { Controller } from "@hotwired/stimulus";
import { drawFrequencyBars } from "@/utilities/visualizer_helpers";

export default class extends Controller {
  static outlets = ["store-audio--recorder"]
  static targets = ["source"]

  disconnect() {
    if (!this.isSupported) return;
    this.#disableAutoPip();
    this.close();
  }

  onRecording(event) {
    if (!this.isSupported) return;

    const status = event.detail.status;
    if (status === "started") {
      this.#enableAutoPip();
    } else if (status === "stoped") {
      this.#disableAutoPip();
      this.close();
    }
  }

  async open() {
    if (window.documentPictureInPicture.window) return;

    this.pip = await window.documentPictureInPicture.requestWindow({
      width: 360,
      height: 100,
    });
    this.pip.document.title = document.title;
    this.#applyStyles(this.pip);

    const card = this.sourceTarget.cloneNode(true);
    this.pip.document.body.append(card);

    this.#wireProxies(card);
    this.#mirror();
    this.#startVisualizer(card);

    this.pip.addEventListener("pagehide", () => this.#teardown());
  }

  close() {
    if (this.pip) {
      this.pip.close();
    } else {
      this.#teardown();
    }
  }

  #enableAutoPip() {
    try {
      navigator.mediaSession.setActionHandler("enterpictureinpicture", () => this.open());
    } catch { }
  }

  #disableAutoPip() {
    try {
      navigator.mediaSession.setActionHandler("enterpictureinpicture", null);
    } catch { }
  }

  #applyStyles(pip) {
    [...document.styleSheets].forEach((sheet) => {
      try {
        const rules = [...sheet.cssRules].map((rule) => rule.cssText).join("");
        const style = document.createElement("style");
        style.textContent = rules;
        pip.document.head.appendChild(style);
      } catch {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.type = sheet.type;
        link.media = sheet.media;
        link.href = sheet.href;
        pip.document.head.appendChild(link);
      }
    });
  }

  #wireProxies(card) {
    this.pairs = ["resume", "pause", "stop"]
      .map((name) => {
        const real = this.#real(name);
        const clone = card.querySelector(`[data-store-audio--screen-modifier-target='${name}']`);
        if (real && clone) {
          clone.removeAttribute("data-action");
          clone.addEventListener("click", () => {
            real.click();
            if (name === "stop") this.close();
            if (name === "pause") this.#freezeVisualizer();
            if (name === "resume") this.#resumeVisualizer();
          });
        }
        return { real, clone };
      })
      .filter(({ real, clone }) => real && clone);
  }

  #mirror() {
    this.observers = this.pairs.map(({ real, clone }) => {
      const sync = () => {
        clone.className = real.className;
        clone.disabled = real.disabled;
      };
      sync();
      const observer = new MutationObserver(sync);
      observer.observe(real, { attributes: true, attributeFilter: ["class", "disabled"] });
      return observer;
    });
  }

  #startVisualizer(card) {
    const stream = this.storeAudioRecorderOutlet.stream;
    this.pipCanvas = card.querySelector("[data-store-audio--visualizer-target='visualizer']");

    this.pipCanvas.parentElement.classList.remove("hidden");
    this.pipCanvas.dataset.state = "play";
    this.pipCanvasContext = this.pipCanvas.getContext("2d");

    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    source.connect(this.analyser);
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    this.#drawLoop();
  }

  #drawLoop() {
    this.analyser.getByteFrequencyData(this.dataArray);
    drawFrequencyBars(this.pipCanvasContext, this.pipCanvas, this.dataArray);
    this.pipAnimationId = this.pip.requestAnimationFrame(() => this.#drawLoop());
  }

  #freezeVisualizer() {
    if (this.pipAnimationId) this.pip.cancelAnimationFrame(this.pipAnimationId);
    this.pipAnimationId = null;
    if (!this.pipCanvas) return;
    this.pipCanvasContext.clearRect(0, 0, this.pipCanvas.width, this.pipCanvas.height);
    this.pipCanvas.dataset.state = "pause";
  }

  #resumeVisualizer() {
    if (!this.analyser || this.pipAnimationId) return;
    this.pipCanvas.dataset.state = "play";
    this.#drawLoop();
  }

  #stopVisualizer() {
    if (this.pipAnimationId) this.pip?.cancelAnimationFrame(this.pipAnimationId);
    this.pipAnimationId = null;
    this.audioContext?.close();
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.pipCanvas = null;
    this.pipCanvasContext = null;
  }

  #teardown() {
    this.#stopVisualizer();
    this.observers?.forEach((observer) => observer.disconnect());
    this.observers = null;
    this.pairs = null;
    this.pip = null;
  }

  #real(name) {
    return this.element.querySelector(`[data-store-audio--screen-modifier-target='${name}']`);
  }

  get isSupported() {
    return "documentPictureInPicture" in window && "mediaSession" in navigator;
  }
}
